/**
 * Behaviour of the Biyaheko landing page, ported from the BIYAHEKO landing export
 * (index.html inline script). Kept close to the original, with these changes:
 *  - every lookup is scoped to the component root (no document-wide queries);
 *  - every listener / timer / animation frame / observer is torn down by the
 *    returned cleanup, so nothing keeps running after the page is left;
 *  - canvas colours come from the theme variables instead of fixed brand reds;
 *  - the "Apply as partner" section is gone: applying happens on the register page
 *    (/signup); the tier buttons hand their tier to it;
 *  - class names that clash with Bootstrap use the bk- prefix (bk-btn, bk-show...).
 */

export interface LandingDeps {
  /** In-app navigation (Angular router). */
  navigate(url: string): void;
  /** Continue to the register page, prefilled with what is known (e.g. the picked tier). */
  apply(details: Partial<LandingApplication>): void;
}

export interface LandingApplication {
  name: string; agency: string; city: string; province: string; mobile: string; email: string;
  status: string; tier: string; package: string; tools: string[]; message: string;
}

export function initLanding(root: HTMLElement, deps: LandingDeps): () => void {
  const RM = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $ = <T extends HTMLElement = HTMLElement>(s: string) => root.querySelector(s) as T;
  const $$ = <T extends HTMLElement = HTMLElement>(s: string) => Array.from(root.querySelectorAll(s)) as T[];

  // ---- teardown bookkeeping
  const cleanups: Array<() => void> = [];
  let destroyed = false;
  const on = (el: EventTarget, type: string, fn: any, opts?: any) => {
    el.addEventListener(type, fn, opts);
    cleanups.push(() => el.removeEventListener(type, fn, opts));
  };
  const every = (fn: () => void, ms: number) => { const id = setInterval(fn, ms); cleanups.push(() => clearInterval(id)); return id; };
  const later = (fn: () => void, ms: number) => { const id = setTimeout(() => { if (!destroyed) fn(); }, ms); cleanups.push(() => clearTimeout(id)); return id; };

  // ---- theme colours for the canvas (CSS variables resolved to rgb)
  const probe = document.createElement('canvas').getContext('2d');
  const toRgb = (cssColor: string, fallback: string): [number, number, number] => {
    const el = document.createElement('span');
    el.style.color = cssColor;
    el.style.display = 'none';
    root.appendChild(el);
    const c = getComputedStyle(el).color || fallback;
    el.remove();
    const m = c.match(/[\d.]+/g);
    if (m && m.length >= 3) return [+m[0], +m[1], +m[2]];
    probe.fillStyle = fallback;
    const hex = String(probe.fillStyle).replace('#', '');
    return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
  };
  const PIN = toRgb('var(--pin)', '#EC1C24');
  const PIN2 = toRgb('var(--pin-2)', '#FF4148');
  const HERO = toRgb('var(--hero)', '#101842');
  const rgba = (c: [number, number, number], a: number) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;

  /* ---------- In-page and in-app links ----------
     The app uses <base href="/">, so href="#tools" would resolve to "/#tools" and
     leave the page; scroll instead. "/..." links go through the router. */
  const scrollToId = (id: string) => {
    if (id === 'top') { window.scrollTo({ top: 0, behavior: RM ? 'auto' : 'smooth' }); return; }
    const el = root.querySelector('#' + CSS.escape(id));
    if (el) el.scrollIntoView({ behavior: RM ? 'auto' : 'smooth', block: 'start' });
  };
  on(root, 'click', (e: MouseEvent) => {
    const a = (e.target as HTMLElement).closest('a') as HTMLAnchorElement;
    if (!a || !root.contains(a) || a.target === '_blank') return;
    const href = a.getAttribute('href') || '';
    if (href.startsWith('#')) { e.preventDefault(); scrollToId(href.slice(1)); }
    else if (href.startsWith('/')) { e.preventDefault(); deps.navigate(href); }
  });

  /* ---------- Nav: mobile menu, active link, flight progress ---------- */
  const menuBtn = $('#menuBtn'), navLinks = $('#navLinks'), nav = $('#nav');
  on(menuBtn, 'click', () => {
    const open = menuBtn.getAttribute('aria-expanded') !== 'true';
    menuBtn.setAttribute('aria-expanded', String(open)); navLinks.classList.toggle('open', open);
    menuBtn.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
  });
  on(navLinks, 'click', (e: MouseEvent) => { if ((e.target as HTMLElement).closest('a')) { menuBtn.setAttribute('aria-expanded', 'false'); navLinks.classList.remove('open'); } });
  const sections = ['new', 'tools', 'tiers', 'join', 'about', 'faq'].map(id => root.querySelector('#' + id) as HTMLElement);
  function onScroll() {
    const h = document.documentElement;
    const p = h.scrollTop / Math.max(1, h.scrollHeight - h.clientHeight);
    nav.style.setProperty('--sp', (p * 100).toFixed(2) + '%');
    let cur = null;
    for (const s of sections) { if (s && s.getBoundingClientRect().top < 140) cur = s.id; }
    $$('.nav-links a').forEach(a => a.classList.toggle('is-active', a.getAttribute('href') === '#' + cur));
    journeyUpdate();
  }
  on(window, 'scroll', onScroll, { passive: true });

  /* ---------- Hero route map ---------- */
  const DESTS: any[] = [
    { n: 'Manila', lon: 121, lat: 14.6, hub: true },
    { n: 'Cebu', lon: 123.9, lat: 10.3, dom: 1 }, { n: 'Boracay', lon: 121.9, lat: 11.97, dom: 1 }, { n: 'Palawan', lon: 119.4, lat: 10.2, dom: 1 }, { n: 'Davao', lon: 125.6, lat: 7.1, dom: 1 },
    { n: 'Tokyo', lon: 139.7, lat: 35.7 }, { n: 'Seoul', lon: 127, lat: 37.5 }, { n: 'Hong Kong', lon: 114.2, lat: 22.3 }, { n: 'Singapore', lon: 103.8, lat: 1.35 },
    { n: 'Bangkok', lon: 100.5, lat: 13.7 }, { n: 'Hanoi', lon: 105.8, lat: 21 }, { n: 'Bali', lon: 115.2, lat: -8.4 }, { n: 'Sydney', lon: 151.2, lat: -33.9 },
    { n: 'Dubai', lon: 55.3, lat: 25.2 }, { n: 'Delhi', lon: 77.2, lat: 28.6 }, { n: 'Istanbul', lon: 29, lat: 41 }, { n: 'Paris', lon: 2.35, lat: 48.9 }, { n: 'London', lon: -0.1, lat: 51.5 }
  ];
  const canvas = $<HTMLCanvasElement>('#routes'), ctx = canvas.getContext('2d');
  let W = 0, H = 0, DPR = 1, view: any, active: string = null;
  const planes: any[] = [];
  function setView() {
    // frame: wide screens show Europe→Australia; Manila sits right of center
    const narrow = W < 760;
    view = narrow ? { l0: 60, l1: 165, t: 48, b: -40 } : { l0: -15, l1: 175, t: 62, b: -42 };
  }
  function proj(lon: number, lat: number): [number, number] {
    const x = (lon - view.l0) / (view.l1 - view.l0) * W;
    const yR = (view.t - lat) / (view.t - view.b);
    return W < 760 ? [x, H * 0.02 + yR * H * 0.36] : [x, H * 0.06 + yR * H * 0.9];
  }
  function resize() {
    const r = canvas.getBoundingClientRect(); DPR = Math.min(2, window.devicePixelRatio || 1);
    W = r.width; H = r.height; canvas.width = Math.round(W * DPR); canvas.height = Math.round(H * DPR);
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0); setView(); if (RM) draw(0);
  }
  function arcPoint(a: number[], b: number[], t: number) {
    const mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2, dx = b[0] - a[0], dy = b[1] - a[1], d = Math.hypot(dx, dy);
    const lift = Math.min(160, d * 0.32);
    const cx = mx + (dy / d || 0) * lift * (dx < 0 ? 1 : -1) * 0.3, cy = my - lift;
    const u = 1 - t; return [u * u * a[0] + 2 * u * t * cx + t * t * b[0], u * u * a[1] + 2 * u * t * cy + t * t * b[1], cx, cy];
  }
  function draw(time: number) {
    ctx.clearRect(0, 0, W, H);
    // grid
    ctx.fillStyle = 'rgba(154,163,199,0.16)';
    for (let lon = Math.ceil(view.l0 / 5) * 5; lon <= view.l1; lon += 5) {
      for (let lat = Math.floor(view.t / 5) * 5; lat >= view.b; lat -= 5) { const [x, y] = proj(lon, lat); ctx.fillRect(x - .8, y - .8, 1.6, 1.6); }
    }
    const hub = proj(DESTS[0].lon, DESTS[0].lat);
    for (const d of DESTS.slice(1)) {
      const p = proj(d.lon, d.lat);
      const isA = active === d.n;
      const [, , cx, cy] = arcPoint(hub, p, 0.5);
      ctx.beginPath(); ctx.moveTo(hub[0], hub[1]); ctx.quadraticCurveTo(cx, cy, p[0], p[1]);
      ctx.strokeStyle = isA ? rgba(PIN2, 0.95) : (active ? 'rgba(154,163,199,0.18)' : 'rgba(154,163,199,0.34)');
      ctx.lineWidth = isA ? 2.4 : 1; ctx.setLineDash(isA ? [] : [3, 5]); ctx.stroke(); ctx.setLineDash([]);
      ctx.beginPath(); ctx.arc(p[0], p[1], isA ? 5 : 3, 0, Math.PI * 2); ctx.fillStyle = isA ? rgba(PIN2, 1) : 'rgba(242,244,251,0.75)'; ctx.fill();
      if (isA) { ctx.beginPath(); ctx.arc(p[0], p[1], 9 + 5 * Math.sin(time / 240), 0, Math.PI * 2); ctx.strokeStyle = rgba(PIN2, 0.45); ctx.lineWidth = 1.5; ctx.stroke(); }
      if ((isA || !d.dom) && p[0] > 8 && p[0] < W - 8 && p[1] > 8 && p[1] < H - 8) {
        ctx.font = (isA ? '600 13px ' : '500 11px ') + '"Figtree", sans-serif';
        ctx.fillStyle = isA ? '#FFFFFF' : 'rgba(242,244,251,0.55)';
        ctx.textAlign = p[0] > hub[0] ? 'left' : 'right';
        const dx = p[0] > hub[0] ? 9 : -9;
        ctx.fillText(d.n.toUpperCase(), p[0] + dx, p[1] - 8);
      }
    }
    // planes
    if (!RM) {
      for (const pl of planes) {
        pl.t += pl.s; if (pl.t > 1) { pl.t = 0; pl.d = DESTS[1 + Math.floor(Math.random() * (DESTS.length - 1))]; }
        if (active && Math.random() < 0.004) pl.d = DESTS.find(x => x.n === active) || pl.d;
        const p = proj(pl.d.lon, pl.d.lat);
        for (let k = 0; k < 10; k++) {
          const tt = Math.max(0, pl.t - k * 0.012); const q = arcPoint(hub, p, tt);
          ctx.beginPath(); ctx.arc(q[0], q[1], 2.4 - k * 0.2, 0, Math.PI * 2);
          // head is white, trail fades out in the brand red
          ctx.fillStyle = k ? rgba(PIN2, (1 - k / 10) * 0.9) : 'rgba(255,255,255,0.9)';
          ctx.fill();
        }
      }
    }
    // hub pin
    ctx.save(); ctx.translate(hub[0], hub[1]);
    ctx.beginPath(); ctx.arc(0, 0, 16 + (RM ? 0 : 6 * Math.sin(time / 400)), 0, Math.PI * 2); ctx.fillStyle = rgba(PIN, 0.16); ctx.fill();
    ctx.translate(0, -2); ctx.beginPath(); ctx.moveTo(0, 0); ctx.bezierCurveTo(-9, -11, -9, -24, 0, -24); ctx.bezierCurveTo(9, -24, 9, -11, 0, 0); ctx.fillStyle = rgba(PIN, 1); ctx.fill();
    ctx.beginPath(); ctx.arc(0, -16, 3.4, 0, Math.PI * 2); ctx.fillStyle = rgba(HERO, 1); ctx.fill();
    ctx.restore();
    ctx.font = '600 12px "Figtree", sans-serif'; ctx.fillStyle = '#FFFFFF'; ctx.textAlign = 'left'; ctx.fillText('MANILA', hub[0] + 14, hub[1] + 4);
  }
  for (let i = 0; i < 7; i++) planes.push({ t: Math.random(), s: 0.0028 + Math.random() * 0.003, d: DESTS[1 + Math.floor(Math.random() * (DESTS.length - 1))] });
  let visible = true;
  try { const io = new IntersectionObserver(es => { visible = es[0].isIntersecting; }); io.observe(canvas); cleanups.push(() => io.disconnect()); } catch (e) { }
  let rafId = 0;
  function loop(t: number) { if (destroyed) return; if (visible && !document.hidden) draw(t); rafId = requestAnimationFrame(loop); }
  cleanups.push(() => cancelAnimationFrame(rafId));
  try { const ro = new ResizeObserver(resize); ro.observe(canvas); cleanups.push(() => ro.disconnect()); } catch (e) { on(window, 'resize', resize); }
  resize();
  if (!RM) rafId = requestAnimationFrame(loop); else draw(0);

  /* Rotating headline word, linked to the map */
  const WORDS: Array<[string, string]> = [['the world.', null], ['Tokyo.', 'Tokyo'], ['Boracay.', 'Boracay'], ['Dubai.', 'Dubai'], ['Seoul.', 'Seoul'], ['Paris.', 'Paris'], ['Cebu.', 'Cebu'], ['Singapore.', 'Singapore'], ['Bali.', 'Bali']];
  let wi = 0; const rot = $('#rot');
  function nextWord() {
    wi = (wi + 1) % WORDS.length; const [w, d] = WORDS[wi];
    rot.innerHTML = ''; const s = document.createElement('span'); s.textContent = w; rot.appendChild(s);
    active = d; if (RM) draw(0);
  }
  if (!RM) every(() => { if (!document.hidden) nextWord(); }, 2600);

  /* ---------- Count-up stats ---------- */
  function countUp(el: HTMLElement) {
    const to = +el.dataset['count'], plain = el.dataset['plain'], start = plain ? to - 40 : 0, dur = 1400, t0 = performance.now();
    function step(t: number) {
      if (destroyed) return;
      const k = Math.min(1, (t - t0) / dur), e = 1 - Math.pow(1 - k, 3), v = Math.round(start + (to - start) * e);
      el.textContent = plain ? String(v) : v.toLocaleString('en-US');
      if (k < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }
  /* ---------- Departure board ---------- */
  const CH = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  function scramble(el: HTMLElement) {
    const final = el.dataset['final'] || (el.dataset['final'] = el.textContent);
    let f = 0; const total = 16;
    function step() {
      f++; const reveal = Math.floor(final.length * f / total);
      el.textContent = final.split('').map((c, i) => i < reveal || c === ' ' || c === '·' ? c : CH[Math.floor(Math.random() * CH.length)]).join('');
      if (f < total) later(step, 38); else el.textContent = final;
    }
    step();
  }
  function flipBoard() { if (RM) return; $$('#boardRows tr:not(.dim) .flap').forEach((el, i) => later(() => scramble(el), i * 40)); }
  $$('.seg button').forEach(b => on(b, 'click', () => {
    $$('.seg button').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
    const f = b.dataset['f'];
    $$('#boardRows tr').forEach(tr => tr.classList.toggle('dim', f !== 'all' && tr.dataset['m'] !== f));
    flipBoard();
  }));
  const clock = $('#clock');
  function tick() { try { clock.textContent = 'MNL ' + new Intl.DateTimeFormat('en-PH', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Manila' }).format(new Date()); } catch (e) { } }
  tick(); every(tick, 30000);

  try {
    const io = new IntersectionObserver(es => es.forEach(en => {
      if (!en.isIntersecting) return; io.unobserve(en.target);
      const t = en.target as HTMLElement;
      if (t.dataset['count']) { if (!RM) countUp(t); }
      else if (t.classList.contains('board')) flipBoard();
    }), { threshold: .4 });
    $$('[data-count]').forEach(el => io.observe(el)); io.observe($('.board'));
    cleanups.push(() => io.disconnect());
  } catch (e) { }

  /* ---------- Toolkit stage ---------- */
  const stage = $('#tk-stage');
  const PANELS: Record<string, () => string> = {
    portal: () => `<div class="tk-panel"><span class="sample">Sample screen · New BK Portal</span><h3>One login. Every booking.</h3>
      <p>Daily travel products in one place, without tab chaos. Booking queues, a passenger calendar, accounts and statements, and staff roles for your team.</p>
      <div class="mock"><div class="mock-row" role="group" aria-label="Product">${['FLIGHTS', 'HOTELS', 'FERRY', 'INSURANCE', 'CRUISES', 'TRAINS'].map((t, i) => `<button type="button" class="mtab" aria-pressed="${i === 0}">${t}</button>`).join('')}</div>
      <div class="msearch"><div><small>FROM</small>MANILA</div><button type="button" class="mtab swap" id="swapBtn" aria-label="Swap origin and destination">⇄</button><div><small>TO</small>CEBU</div><button type="button" class="go" id="goBtn">Search</button></div>
      <div class="mlist" id="queue"><div class="mitem"><span>Booking queue · Confirmed</span><span class="st ok">CONFIRMED</span></div><div class="mitem"><span>Booking queue · On hold</span><span class="st hold">ON HOLD</span></div><div class="mitem"><span>Booking queue · In progress</span><span class="st prog">IN PROGRESS</span></div></div></div></div>`,
    pos: () => `<div class="tk-panel"><span class="sample">New · Tap the keys · Biyaheko Pay Super POS</span><h3>Earn every day from payments.</h3>
      <div class="pos"><div class="device"><div class="screen" id="posScreen"><small>BIYAHEKO PAY</small><b>Tap a service to see what your counter can do.</b></div>
      <div class="keys" role="group" aria-label="POS services">${[['card', 'Card'], ['qr', 'QR Ph'], ['cin', 'Cash in'], ['cout', 'Cash out'], ['bills', 'Bills'], ['load', 'E-load']].map(k => `<button type="button" class="key" data-k="${k[0]}" aria-pressed="false">${k[1]}</button>`).join('')}</div></div>
      <div class="pos-copy"><p style="color:var(--hero-dim)">An all-in-one payment terminal for your counter, so you earn from everyday transactions, even in the low season.</p>
      <ul><li>Debit cards, QR Ph, GCash and Maya</li><li>Cash in and cash out for walk-in customers</li><li>700+ billers and e-load</li><li>Works on WiFi or with an optional Super SIM</li></ul></div></div></div>`,
    academy: () => `<div class="tk-panel"><span class="sample">Tap a course · BK Academy</span><h3>Sell with confidence from day one.</h3>
      <p>Training that builds product understanding, platform mastery and sales confidence.</p>
      <div class="mock-row"><a class="bk-btn btn-pin" href="/home/academy">See BK Academy packages <span class="arr">→</span></a></div>
      <div class="courses">${[['Product training', 'Know what you sell'], ['Platform mastery', 'Book faster in BK Portal'], ['Selling scripts', 'Turn inquiries into bookings'], ['Inbound handling', 'Serve foreign and balikbayan clients']].map((c, i) => `<button type="button" class="course" aria-pressed="${i === 0}"><b>${c[0]}</b><small>${c[1].toUpperCase()}</small><span class="bar"><i style="width:${i === 0 ? 100 : 0}%"></i></span></button>`).join('')}</div></div>`,
    media: () => `<div class="tk-panel"><span class="sample">Sample posts · BK Media</span><h3>Look professional. Stay visible.</h3>
      <p>Ready-to-use banners, social posts and videos for your page, so your clients always see you.</p>
      <div class="banners"><div class="bn bn1"><small>FLASH SEAT SALE</small>Fly to Cebu this weekend.</div><div class="bn bn2"><small>VISA ASSISTANCE</small>Japan visa? We'll help.</div><div class="bn bn3"><small>GROUP DEPARTURE</small>Seoul in spring.</div><div class="bn bn4"><small>BORACAY</small>White Beach is calling.</div><div class="bn bn5"><small>TRAVEL INSURANCE</small>Travel worry-free.</div><div class="bn bn6"><small>BOHOL</small>See the Chocolate Hills.</div></div></div>`,
    support: () => `<div class="tk-panel"><span class="sample">How it feels · BK Support</span><h3>We've got your back.</h3>
      <p>Fast, responsive help before, during and after every booking, because your reputation is on the line.</p>
      <div class="chat" id="chat"><div class="bubble bk-me"><small>YOU</small>My client needs to change their hotel dates. Can you help?</div><div class="bubble bk"><small>BK SUPPORT</small>Of course! Send us the booking and we'll take care of it with you.</div></div>
      <div class="chat-actions"><button type="button" class="mtab" data-say="before">Before booking</button><button type="button" class="mtab" data-say="during">During the trip</button><button type="button" class="mtab" data-say="after">After the trip</button></div></div>`,
    matik: () => `<div class="tk-panel"><span class="sample">New · BIYAHEKOMATIK</span><h3>Tour and visa quotes in minutes.</h3>
      <p>Outbound and domestic packages, live group-departure slots, your commission per package and visa quotes with your own markup, shared across your agency.</p>
      <div class="mock-row"><a class="bk-btn btn-pin" href="#new">Try the live demo <span class="arr">→</span></a><a class="bk-btn btn-ghost" href="https://biyahekomatik.biyaheko.com" target="_blank" rel="noopener">Open the app</a></div></div>`
  };
  const POS: Record<string, string> = { card: 'Accept debit cards at your counter.', qr: 'Accept QR Ph, GCash and Maya payments.', cin: 'Cash in for your walk-in customers.', cout: 'Cash out right at your counter.', bills: 'Pay 700+ billers in one place.', load: 'Sell e-load to your customers.' };
  const SAY: Record<string, [string, string]> = { before: ['Which visa does my client need for Japan?', 'We\'ll walk you through it, and you can quote it in BIYAHEKOMATIK.'], during: ['My client\'s ferry was rebooked. What now?', 'We\'re on it. Here are the new details for your client.'], after: ['Can you send the documents for my client\'s refund?', 'Sending them to you now. Salamat for your patience!'] };
  // Panel content is rebuilt on every tab switch, so its controls are wired with
  // one delegated listener on the stage (nothing to unbind per panel).
  on(stage, 'click', (e: MouseEvent) => {
    const t = e.target as HTMLElement;
    const mtab = t.closest('.mock-row .mtab') as HTMLElement;
    if (mtab) { $$('#tk-stage .mock-row .mtab').forEach(x => x.setAttribute('aria-pressed', String(x === mtab))); return; }
    if (t.closest('#swapBtn')) {
      const ds = $$('#tk-stage .msearch > div'); const a = ds[0].lastChild.textContent; ds[0].lastChild.textContent = ds[1].lastChild.textContent; ds[1].lastChild.textContent = a; return;
    }
    if (t.closest('#goBtn')) {
      const q = $('#queue'); q.style.opacity = '.3';
      later(() => { q.style.opacity = '1'; (q.firstElementChild.firstElementChild as HTMLElement).textContent = 'Search done · ready to book'; }, 450); return;
    }
    const key = t.closest('.key') as HTMLElement;
    if (key) {
      $$('.key').forEach(x => x.setAttribute('aria-pressed', String(x === key)));
      $('#posScreen').innerHTML = '<small>' + key.textContent.toUpperCase() + '</small><b>' + POS[key.dataset['k']] + '</b>'; return;
    }
    const course = t.closest('.course') as HTMLElement;
    if (course) {
      $$('.course').forEach(x => x.setAttribute('aria-pressed', String(x === course)));
      (course.querySelector('.bar i') as HTMLElement).style.width = '100%'; return;
    }
    const say = t.closest('[data-say]') as HTMLElement;
    if (say) {
      const [q, a] = SAY[say.dataset['say']]; const chat = $('#chat');
      chat.innerHTML = '<div class="bubble bk-me"><small>YOU</small></div>'; (chat.firstChild as HTMLElement).append(q);
      later(() => { const d = document.createElement('div'); d.className = 'bubble bk'; d.innerHTML = '<small>BK SUPPORT</small>'; d.append(a); chat.appendChild(d); }, 500);
    }
  });
  function showTab(t: string) {
    $$('.tk-tab').forEach(b => b.setAttribute('aria-selected', String(b.dataset['t'] === t)));
    stage.setAttribute('aria-labelledby', 'tab-' + t);
    stage.innerHTML = PANELS[t]();
  }
  const tabs = $$('.tk-tab');
  tabs.forEach((b, i) => {
    on(b, 'click', () => showTab(b.dataset['t']));
    on(b, 'keydown', (e: KeyboardEvent) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      e.preventDefault(); const n = tabs[(i + (e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length]; n.focus(); showTab(n.dataset['t']);
    });
  });
  showTab('portal');

  /* ---------- BIYAHEKOMATIK demo ---------- */
  const TRIPS: Record<string, any> = {
    japan: { name: 'Japan Discovery', city: 'Tokyo', intl: true, visa: 'Japan tourist visa', days: ['Tokyo city tour: Asakusa and Shibuya', 'Mt. Fuji and Lake Kawaguchi day trip', 'Free day for shopping in Ginza', 'Bullet train to Kyoto, temple walk', 'Osaka food crawl in Dotonbori', 'Nara deer park side trip'] },
    korea: { name: 'Seoul Highlights', city: 'Seoul', intl: true, visa: 'Korea tourist visa', days: ['Gyeongbokgung Palace and Bukchon', 'Nami Island day trip', 'Myeongdong shopping', 'Theme park day', 'N Seoul Tower at night', 'Hongdae and Ewha street food'] },
    vietnam: { name: 'Northern Vietnam', city: 'Hanoi', intl: true, visa: null, days: ['Hanoi Old Quarter walk', 'Ha Long Bay cruise', 'Ninh Binh boat ride', 'Hoa Lo and the Temple of Literature', 'Free day for coffee and shopping', 'Water puppet show'] },
    boracay: { name: 'Boracay Escape', city: 'Boracay', intl: false, visa: null, days: ['White Beach sunset', 'Island hopping', 'Puka Beach and Ilig-Iligan', 'Free day for water sports', 'Spa and beach day', 'Mt. Luho viewpoint'] },
    bohol: { name: 'Bohol Countryside', city: 'Bohol', intl: false, visa: null, days: ['Chocolate Hills', 'Tarsier sanctuary', 'Loboc River lunch cruise', 'Panglao beach day', 'Balicasag island hopping', 'Baclayon Church heritage stop'] }
  };
  const st: any = { d: 'japan', days: 5, pax: 2, visa: true };
  const quote = $('#quote'), visaT = $<HTMLInputElement>('#visaT'), visaLbl = $('#visaLbl');
  let qTimer: any = null;
  function renderQuote(instant?: boolean) {
    const t = TRIPS[st.d]; const hasVisa = !!t.visa;
    visaT.disabled = !hasVisa; visaLbl.textContent = hasVisa ? 'Add visa assistance (' + t.visa + ')' : (t.intl ? 'No visa needed for Filipino travelers' : 'Domestic trip: no visa needed');
    const items = ['Hotel', 'Transfers', 'Tours', t.intl ? 'Flights' : 'Flights or ferry', 'Travel insurance'];
    const days = [];
    for (let i = 1; i <= st.days; i++) {
      const txt = i === 1 ? 'Arrive in ' + t.city + ', hotel check-in' : i === st.days ? 'Free morning, transfer for departure' : t.days[(i - 2) % t.days.length];
      days.push(`<li style="animation-delay:${(i - 1) * 60}ms"><b>DAY ${i}</b><span>${txt}</span></li>`);
    }
    const body = `<div class="q-head"><div><h4>${t.name}</h4><p>${st.days}D${st.days - 1}N · ${st.pax} traveler${st.pax > 1 ? 's' : ''} · ${t.intl ? 'International' : 'Domestic'}</p></div><span class="stamp">QUOTE READY</span></div>
      <ul class="q-days">${days.join('')}</ul>
      <div class="q-inc">${items.map(x => `<span>${x}</span>`).join('')}${hasVisa && st.visa ? `<span class="v">${t.visa}</span>` : ''}</div>
      <div class="q-foot"><div><b>Price</b>From live package tariff</div><div><b>Your commission</b>Shown per package</div></div>`;
    clearTimeout(qTimer);
    if (instant || RM) { quote.innerHTML = body; return; }
    quote.innerHTML = body + '<div class="q-loading"><div style="text-align:center">BUILDING QUOTE…<div class="lb"><i></i></div></div></div>';
    qTimer = later(() => { const l = quote.querySelector('.q-loading'); if (l) l.remove(); }, 720);
  }
  $$('.dest').forEach(b => on(b, 'click', () => { $$('.dest').forEach(x => x.setAttribute('aria-pressed', String(x === b))); st.d = b.dataset['d']; renderQuote(); }));
  function bump(k: string, delta: number, min: number, max: number, out: string) { st[k] = Math.max(min, Math.min(max, st[k] + delta)); $(out).textContent = st[k]; renderQuote(); }
  on($('#dMinus'), 'click', () => bump('days', -1, 3, 8, '#dOut'));
  on($('#dPlus'), 'click', () => bump('days', 1, 3, 8, '#dOut'));
  on($('#pMinus'), 'click', () => bump('pax', -1, 1, 20, '#pOut'));
  on($('#pPlus'), 'click', () => bump('pax', 1, 1, 20, '#pOut'));
  on(visaT, 'change', () => { st.visa = visaT.checked; renderQuote(); });
  renderQuote(true);

  /* ---------- Tier finder ---------- */
  const ans: any = { sub: null, brand: null };
  $$('.yn button').forEach(b => on(b, 'click', () => {
    const q = b.dataset['q']; ans[q] = b.dataset['v'] === '1';
    $$('.yn button[data-q="' + q + '"]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
    let tier = null;
    if (ans.brand === true) tier = 'White-label + sub-agents';
    else if (ans.sub === true) tier = 'Master agent + sub-agents';
    else if (ans.sub === false && ans.brand === false) tier = 'Direct agent';
    else if (ans.sub === false && ans.brand === null) tier = 'Direct agent';
    const grid = $('#tiersGrid'); grid.classList.toggle('has-match', !!tier);
    $$('.tier').forEach(t => t.classList.toggle('match', t.dataset['tier'] === tier));
  }));
  // "Apply as <tier>" goes to the register page with that tier (it lands in remarks).
  $$('[data-apply]').forEach(b => on(b, 'click', () => deps.apply({ tier: b.dataset['apply'] })));

  /* ---------- Journey ---------- */
  const journey = $('#journey'), jsteps = $$('.jstep');
  function journeyUpdate() {
    const r = journey.getBoundingClientRect(), vh = window.innerHeight;
    let p = (vh * 0.82 - r.top) / (r.height + vh * 0.25); p = Math.max(0, Math.min(1, p));
    journey.style.setProperty('--p', p.toFixed(3));
    jsteps.forEach((s, i) => s.classList.toggle('on', p >= i / (jsteps.length - 1) - 0.02));
  }
  onScroll();

  /* ---------- Values ---------- */
  const VALUES: Array<[string, string, string, string]> = [
    ['B', 'Bayanihan Partnerships', 'We journey hand-in-hand with agencies, suppliers and global partners to win together.', 'collaborating openly, communicating clearly, building long-term relationships.'],
    ['I', 'Integrity Builds Trust', 'We do what\'s right, always, because trust is our strongest advantage.', 'being transparent, honoring commitments, protecting data and payments.'],
    ['Y', 'Yakap Service (Malasakit)', 'We care deeply and respond fast, because behind every booking is a business and a traveler.', 'being proactive, solution-first and calm under pressure.'],
    ['A', 'Agile Innovation', 'We innovate with purpose: to simplify distribution, accelerate bookings and remove friction.', 'improving continuously, listening to partners, building easy-to-use tools.'],
    ['H', 'Humble Hearts', 'We stay grounded as we grow, learning, improving and respecting every partner\'s journey.', 'staying teachable, giving credit, serving with sincerity.'],
    ['E', 'Excellence in Execution', 'To be No. 1 and most trusted, we must be dependable every day, at any scale.', 'being accurate, consistent, process-driven and quality-obsessed.'],
    ['K', 'Kaagapay Empowerment', 'We exist to empower travel professionals to sell more, serve better and grow faster.', 'enabling with tools and training, sharing best practices, removing bottlenecks.'],
    ['O', 'Open Horizons', 'We represent the Philippines with world-class standards and welcome global partners.', 'thinking globally, acting professionally, bringing Filipino hospitality to every interaction.']
  ];
  const letters = $('#letters'), vpanel = $('#vpanel');
  let vi = 0, cyc: any = null;
  VALUES.forEach((v, i) => {
    const b = document.createElement('button'); b.type = 'button'; b.className = 'letter' + (v[0] === 'O' ? ' o' : ''); b.setAttribute('role', 'tab'); b.id = 'val-' + i;
    b.setAttribute('aria-label', v[1]); b.innerHTML = v[0] + '<span class="tick"><i></i></span>';
    on(b, 'click', () => { stopCycle(); setVal(i); });
    letters.appendChild(b);
  });
  function setVal(i: number) {
    vi = i; $$('.letter').forEach((b, j) => b.setAttribute('aria-selected', String(j === i)));
    const v = VALUES[i];
    vpanel.setAttribute('aria-labelledby', 'val-' + i);
    vpanel.innerHTML = `<span class="big">${v[0]}</span><h3>${v[1]}</h3><p>${v[2]}</p><p class="bk-show"><b>We show it by</b>${v[3]}</p>`;
  }
  function stopCycle() { clearInterval(cyc); cyc = null; letters.classList.add('paused'); }
  setVal(0);
  if (!RM) {
    cyc = every(() => { if (!document.hidden) setVal((vi + 1) % VALUES.length); }, 4200);
  } else letters.classList.add('paused');

  /* ---------- Agent story videos ---------- */
  $$('.story .play').forEach(btn => on(btn, 'click', () => {
    const v = $<HTMLVideoElement>('#' + btn.dataset['v']);
    $$<HTMLVideoElement>('.story video').forEach(o => { if (o !== v) o.pause(); });
    btn.hidden = true; v.controls = true; v.play().catch(() => { btn.hidden = false; });
  }));
  $$<HTMLVideoElement>('.story video').forEach(v => on(v, 'play', () => {
    $$<HTMLVideoElement>('.story video').forEach(o => { if (o !== v) o.pause(); });
    const b = v.parentElement.querySelector('.play') as HTMLElement; if (b) b.hidden = true; v.controls = true;
  }));
  cleanups.push(() => $$<HTMLVideoElement>('.story video').forEach(v => v.pause()));

  /* ---------- Event countdown (WTM London, 3 Nov 2026, 10:00 London) ---------- */
  (function () {
    const start = Date.UTC(2026, 10, 3, 10, 0), end = Date.UTC(2026, 10, 6, 0, 0);
    const ev = $('#event'), chip = $('#eventChip');
    function upd() {
      const now = Date.now();
      if (now >= end) { if (ev) ev.hidden = true; if (chip) chip.hidden = true; return; }
      let ms = Math.max(0, start - now);
      const d = Math.floor(ms / 864e5); ms -= d * 864e5; const h = Math.floor(ms / 36e5); ms -= h * 36e5; const m = Math.floor(ms / 6e4);
      const set = (id: string, v: number) => { const el = $('#' + id); if (el) el.textContent = String(v).padStart(2, '0'); };
      set('cdD', d); set('cdH', h); set('cdM', m);
      if (now >= start) { const c = $('#countdown'); if (c) c.innerHTML = '<div style="min-width:auto"><b>HAPPENING NOW</b><span>STAND N9-200</span></div>'; }
    }
    upd(); every(upd, 30000);
  })();

  return () => {
    destroyed = true;
    cleanups.forEach(fn => { try { fn(); } catch (e) { } });
    cleanups.length = 0;
  };
}
